import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'react-bootstrap';
import AboutUsSectionImg from '../../utils/images/about-us-section-img.jpg';
import './About.css';

// ── Data — all original content preserved ─────────────────────────────────────
const whyUs = [
    {
        icon: '💡',
        title: 'Fresh Perspective',
        desc: "As a startup, we bring a fresh perspective to lead generation. We're not bound by traditional norms or outdated practices. Instead, we're constantly exploring new ideas, technologies, and strategies to deliver innovative solutions that drive real results for our clients.",
    },
    {
        icon: '⚡',
        title: 'Agility and Adaptability',
        desc: "Startups are known for their agility and adaptability. We thrive in fast-paced environments, where change is constant and flexibility is key. This agility allows us to quickly pivot and adjust our strategies based on evolving market trends and client needs.",
    },
    {
        icon: '❤️',
        title: 'Passion and Dedication',
        desc: "At LeadsQResearch, lead generation isn't just a job — it's our passion. We're deeply committed to helping our clients succeed, and that dedication shines through in everything we do, from meticulous attention to detail to unwavering commitment to results.",
    },
    {
        icon: '🎯',
        title: 'Personalized Attention',
        desc: "Unlike larger agencies where you might feel like just another client, you're more than just a number at LeadsQResearch. We take the time to truly understand your business, goals, and target audience, developing customized lead-generation strategies tailored to you.",
    },
    {
        icon: '🌐',
        title: 'Market Research',
        desc: "As a global market research firm, LeadsQResearch delivers thorough and precise studies of diverse global markets. Leveraging multiple data sources, our strategies provide a holistic understanding of current trends, anticipated changes, and actionable recommendations.",
    },
];

const stats = [
    { value: '2+', label: 'Years Experience' },
    { value: '15+', label: 'Happy Clients' },
    { value: '500+', label: 'Leads Generated' },
    { value: '99%', label: 'Satisfaction Rate' },
];

// ── Count-up hook ─────────────────────────────────────────────────────────────
const useCountUp = (target, isActive, duration = 1600) => {
    const [count, setCount] = useState(0);
    useEffect(() => {
        if (!isActive) return;
        const numeric = parseFloat(String(target).replace(/[^0-9.]/g, ''));
        const suffix = String(target).replace(/[0-9.]/g, '');
        let start = null;
        const step = (ts) => {
            if (!start) start = ts;
            const p = Math.min((ts - start) / duration, 1);
            const e = 1 - Math.pow(1 - p, 3);
            setCount(Math.floor(e * numeric) + suffix);
            if (p < 1) requestAnimationFrame(step);
            else setCount(target);
        };
        requestAnimationFrame(step);
    }, [isActive, target, duration]);
    return count;
};

// ── Stat item (hook must be at component top level) ───────────────────────────
const StatItem = ({ value, label, index, visible }) => {
    const val = useCountUp(value, visible);
    return (
        <div className="ab-stat" style={{ '--i': index }}>
            <span className="ab-stat__value">{val}</span>
            <span className="ab-stat__label">{label}</span>
        </div>
    );
};

// ── Why-us card ───────────────────────────────────────────────────────────────
const WhyCard = ({ item, index, visible }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <Col lg={4} md={6}
            className={`ab-why-col ${visible ? 'ab-why-col--visible' : ''}`}
            style={{ '--i': index }}
        >
            <div
                className={`ab-why-card ${hovered ? 'ab-why-card--hovered' : ''}`}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                <div className="ab-why-card__glow" />
                <div className="ab-why-card__icon-wrap">
                    <span className="ab-why-card__icon">{item.icon}</span>
                </div>
                <h4 className="ab-why-card__title">{item.title}</h4>
                <p className="ab-why-card__desc">{item.desc}</p>
                <div className="ab-why-card__line" />
            </div>
        </Col>
    );
};

// ── Main ──────────────────────────────────────────────────────────────────────
export default function About() {
    const [heroVisible, setHeroVisible] = useState(false);
    const [statsVisible, setStatsVisible] = useState(false);
    const [storyVisible, setStoryVisible] = useState(false);
    const [whyVisible, setWhyVisible] = useState(false);

    const statsRef = useRef(null);
    const storyRef = useRef(null);
    const whyRef = useRef(null);

    // Hero entrance
    useEffect(() => {
        const t = setTimeout(() => setHeroVisible(true), 80);
        return () => clearTimeout(t);
    }, []);

    // Scroll observers
    useEffect(() => {
        const makeObs = (ref, setter) => {
            const o = new IntersectionObserver(
                ([e]) => { if (e.isIntersecting) setter(true); },
                { threshold: 0, rootMargin: '0px 0px -60px 0px' }
            );
            if (ref.current) o.observe(ref.current);
            return o;
        };
        const o1 = makeObs(statsRef, setStatsVisible);
        const o2 = makeObs(storyRef, setStoryVisible);
        const o3 = makeObs(whyRef, setWhyVisible);
        return () => { o1.disconnect(); o2.disconnect(); o3.disconnect(); };
    }, []);

    return (
        <div className="about-page">

            {/* ════════════════════════════════
          HERO
      ════════════════════════════════ */}
            <header className={`ab-header height-75 ${heroVisible ? 'ab-header--visible' : ''}`}>
                <div className="ab-header__bg" />
                <div className="ab-header__grid" />
                <div className="ab-header__orb ab-header__orb--1" />
                <div className="ab-header__orb ab-header__orb--2" />

                <Container className="ab-header__content">
                    <div className="ab-hero-badge" style={{ '--delay': '0s' }}>
                        <span className="ab-hero-badge__dot" />
                        Our Story
                    </div>

                    <h1 className="ab-hero-title" style={{ '--delay': '0.18s' }}>
                        About <span className="ab-hero-accent">LeadsQ</span>Research
                    </h1>

                    <div className="ab-hero-line" style={{ '--delay': '0.32s' }} />

                    <p className="ab-hero-text" style={{ '--delay': '0.42s' }}>
                        When you choose LeadsQResearch for your lead generation needs, you're not just hiring
                        another vendor — you're partnering with a dedicated team of experts who are invested
                        in your success. With our innovative approach, agile mindset, and unwavering commitment
                        to excellence, we're confident that we can help take your business to the next level.
                    </p>

                    <div className="ab-hero-ctas" style={{ '--delay': '0.56s' }}>
                        <Link to="/contact" className="ab-btn ab-btn--primary">
                            Get In Touch <span className="ab-btn__arrow">→</span>
                        </Link>
                        <Link to="/courses" className="ab-btn ab-btn--outline">
                            Our Services
                        </Link>
                    </div>
                </Container>

                <div className="ab-header__scroll">
                    <span>Scroll</span>
                    <div className="ab-header__scroll-line" />
                </div>
            </header>

            {/* ════════════════════════════════
          STATS BAR
      ════════════════════════════════ */}
            <div className="ab-stats-wrapper" ref={statsRef}>
                <Container>
                    <div className={`ab-stats ${statsVisible ? 'ab-stats--visible' : ''}`}>
                        {stats.map((s, i) => (
                            <StatItem key={i} value={s.value} label={s.label} index={i} visible={statsVisible} />
                        ))}
                    </div>
                </Container>
            </div>

            {/* ════════════════════════════════
          STORY / IMAGE SECTION
      ════════════════════════════════ */}
            <div
                className={`ab-story ${storyVisible ? 'ab-story--visible' : ''}`}
                ref={storyRef}
            >
                <Container>
                    <Row className="align-items-center g-5">

                        {/* Image column */}
                        <Col lg={5} className="ab-story__img-col">
                            <div className="ab-story__img-wrap">
                                <img
                                    src={AboutUsSectionImg}
                                    alt="About LeadsQResearch team"
                                    className="ab-story__img"
                                />
                                <div className="ab-story__img-border" />
                                <div className="ab-story__img-glow" />
                                <div className="ab-story__badge">
                                    <span className="ab-story__badge-value">2+</span>
                                    <span className="ab-story__badge-label">Years of Excellence</span>
                                </div>
                            </div>
                        </Col>

                        {/* Text column */}
                        <Col lg={7} className="ab-story__text-col">
                            <span className="ab-label">✦ Why Choose Us</span>
                            <h2 className="ab-title">Why a Startup Like Ours?</h2>
                            <p className="ab-text">
                                Why choose a startup for your lead generation needs? We get it — it's a valid
                                question. Here's why we believe startups like ours offer a unique advantage over
                                traditional agencies. We combine the precision of seasoned researchers with the
                                hunger of a team that has something to prove.
                            </p>

                            {/* Inline why-us list for the story section */}
                            <div className="ab-story__points">
                                {whyUs.map((w, i) => (
                                    <div key={i} className="ab-story__point">
                                        <span className="ab-story__point-icon">{w.icon}</span>
                                        <div>
                                            <strong className="ab-story__point-title">{w.title}</strong>
                                            <p className="ab-story__point-desc">{w.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <Link to="/contact" className="ab-btn ab-btn--primary mt-4 d-inline-flex">
                                Contact Us <span className="ab-btn__arrow">→</span>
                            </Link>
                        </Col>

                    </Row>
                </Container>
            </div>

            {/* ════════════════════════════════
          WHY-US CARDS
      ════════════════════════════════ */}
            <div className="ab-why-section" ref={whyRef}>
                <Container>
                    <div className={`ab-why-header ${whyVisible ? 'ab-why-header--visible' : ''}`}>
                        <span className="ab-label">✦ Our Advantage</span>
                        <h2 className="ab-title">What Sets Us Apart</h2>
                        <p className="ab-subtitle">
                            Five pillars that define how we work and why clients keep coming back.
                        </p>
                    </div>
                    <Row className="g-4">
                        {whyUs.map((item, i) => (
                            <WhyCard key={i} item={item} index={i} visible={whyVisible} />
                        ))}
                    </Row>
                </Container>
            </div>

        </div>
    );
}
